import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

/**
 * Pressure Certificate PDF — Template compatible with:
 * CPH 6000 r14 / CPH 6600 r09 pressure manometer flows.
 *
 * IMPORTANT:
 * The Excel-generated phrase:
 *   "FUNCIONA, PERO NO ESTÁ DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE"
 * MUST NOT be printed.
 */

const LOGO_URL = '/logo-calins-color-hd.png';
const WATERMARK_LOGO_URL = '/logo-calins.png';
const ENABLE_WATERMARK = false;
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

// -----------------------------
// Helpers
// -----------------------------

function asNumber(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}

function fmtNumber(v, decimals = 3) {
  const x = asNumber(v);
  if (x === null) return '-';
  return x.toFixed(decimals).replace('.', ',');
}

function fmtPct(v, decimals = 2) {
  const x = asNumber(v);
  if (x === null) return '-';
  return `${x.toFixed(decimals).replace('.', ',')}%`;
}

// Código del procedimiento técnico de calibración de presión (va en el certificado).
const PROCEDURE_CODE = 'P-DT-PT-01';

function dmy(value) {
  if (!value) return '-';
  // Una fecha sin hora ("2027-01-21") se parsea como medianoche UTC y, al mostrarla
  // en hora local (UTC-3), retrocedía un día. Se ancla al mediodía local.
  const s = String(value);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
  const d = new Date(dateOnly ? `${s}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

async function fetchImageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function withOpacity(doc, alpha) {
  if (typeof doc.addGState !== 'function' || typeof doc.setGState !== 'function') return false;
  try {
    const key = `WM_${Math.round(alpha * 100)}`;
    doc.addGState(key, { opacity: alpha, strokeOpacity: alpha });
    doc.setGState(key);
    return true;
  } catch {
    return false;
  }
}

function drawWatermark(doc, logoDataUrl) {
  if (!logoDataUrl) return;
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.saveGraphicsState();
  const usedOpacity = withOpacity(doc, 0.06);
  const markW = 130;
  const markH = 65;
  const x = (w - markW) / 2;
  const y = (h - markH) / 2;
  doc.addImage(logoDataUrl, 'PNG', x, y, markW, markH, undefined, 'FAST');
  if (usedOpacity) withOpacity(doc, 1);
  doc.restoreGraphicsState();
}

function drawFooterTemplate(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function drawHeaderTemplate(doc, { logoDataUrl, certificateNumber, page, pages, variant }) {
  drawCertificateHeader(doc, {
    logo: logoDataUrl,
    number: certificateNumber,
    page,
    pages,
    first: variant === 'page1',
  });
}

function drawLegalParagraph(doc, x, y, maxWidth) {
  return drawCertificateLegalText(doc, y, { x, width: maxWidth, fontSize: 10 });
}

function drawPage1(doc, { session, instrument }) {
  const unit = session.instrument_unit || 'bar';
  const w = doc.internal.pageSize.getWidth();

  let y = 66;
  y = drawLegalParagraph(doc, 18, y, w - 36) + 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('ESPECIFICACIONES', 14, y);
  y += 7;

  const colL = 14;
  const colV = 70;
  const colR = 110;
  const colRV = w - 14; // margen derecho real
  const rowH = 5.2;
  doc.setFontSize(10);

  const leftRows = [
    { k: 'Objeto:', v: session.instrument_object || instrument?.type || 'MANOMETRO' },
    { k: 'Fabricante:', v: session.instrument_brand || instrument?.brand || '-' },
    { k: 'Modelo:', v: session.instrument_model || instrument?.model || '-' },
    { k: 'Serie Número:', v: session.instrument_serial || instrument?.serial || '-' },
    { k: 'TAG:', v: session.instrument_tag || instrument?.tag || '-' },
    { k: 'Determinaciones requeridas:', v: 'Calibración' },
    { k: 'Fecha de Calibración o Medición:', v: dmy(session.calibration_date) },
  ];

  const rightRows = [
    { k: 'Ubicación:', v: session.instrument_location || '-' },
    {
      k: 'Clase:',
      v: session.instrument_class
        ? `${String(session.instrument_class).replace('.', ',')}  [%]`
        : session.instrument_accuracy_class
          ? `${String(session.instrument_accuracy_class).replace('.', ',')}  [%]`
          : '-',
    },
    { k: 'Resolución:', v: `${fmtNumber(session.instrument_resolution ?? instrument?.resolution, 2)}  [${unit}]` },
    { k: 'Menor División:', v: `${fmtNumber(session.instrument_resolution ?? instrument?.resolution, 2)}  [${unit}]` },
    {
      k: 'Intervalo:',
      v: `${fmtNumber(session.instrument_lrl ?? instrument?.lrl, 0)}  a  ${fmtNumber(session.instrument_url ?? instrument?.url, 0)}  [${unit}]`,
    },
    { k: 'Fecha de emisión de Informe:', v: dmy(session.report_issue_date || new Date().toISOString()) },
    // La sesión de presión guarda el vencimiento en expiration_date (no valid_until):
    // se leen ambos para que la validez nunca salga vacía.
    { k: 'Validez :', v: dmy(session.valid_until || session.expiration_date) },
    { k: 'Procedimiento de calibración:', v: PROCEDURE_CODE },
  ];

  const startY = y;
  let yy = y;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  leftRows.forEach((r) => {
    doc.text(r.k, colL, yy);
    yy += rowH;
  });
  yy = startY;
  rightRows.forEach((r) => {
    doc.text(r.k, colR, yy);
    yy += rowH;
  });

  yy = startY;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  leftRows.forEach((r) => {
    doc.text(String(r.v ?? '-'), colV, yy);
    yy += rowH;
  });
  yy = startY;
  rightRows.forEach((r) => {
    doc.text(String(r.v ?? '-'), colRV, yy, { align: 'right' });
    yy += rowH;
  });

  // CLIENTE
  y = startY + rowH * Math.max(leftRows.length, rightRows.length) + 10;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CLIENTE', 14, y);
  y += 9;

  const clientColK = 46;
  const clientColV = 92;
  const clientRows = [
    { k: 'Razón Social:', v: session.client_name || '-' },
    { k: 'Dirección:', v: session.client_address || '-' },
    { k: 'Ciudad/Dpto.:', v: session.client_city || '-' },
    { k: 'Provincia:', v: session.client_province || '-' },
  ];
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  yy = y;
  clientRows.forEach((r) => {
    doc.text(r.k, clientColK, yy);
    yy += rowH;
  });
  yy = y;
  clientRows.forEach((r, i) => {
    doc.setFont('helvetica', i === 0 ? 'bolditalic' : 'normal');
    doc.text(String(r.v ?? '-'), clientColV, yy);
    yy += rowH;
  });

  // CONDICIONES AMBIENTALES
  y = y + rowH * clientRows.length + 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CONDICIONES AMBIENTALES', 14, y);
  y += 10;

  const envXK = 74;
  const envXV = 128;
  const envRows = [
    {
      k: 'Presión atmosférica:',
      v: fmtNumber(session.ambient_pressure_avg ?? session.ambient_pressure ?? 0, 1),
      u: '[hPa] ± 1 [hPa]',
    },
    {
      k: 'Temperatura Ambiente:',
      v: fmtNumber(session.ambient_temp_avg ?? session.ambient_temp ?? 0, 1),
      u: '[ºC] ± 1 [ºC]',
    },
    {
      k: 'Humedad Relativa:',
      v: fmtNumber(session.ambient_humidity_avg ?? session.ambient_humidity ?? 0, 1),
      u: '[% H.R.] ± 3 [% H.R.]',
    },
  ];

  doc.setFontSize(10);
  yy = y;
  envRows.forEach((r) => {
    doc.setFont('helvetica', 'bold');
    doc.text(r.k, envXK, yy, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.text(String(r.v ?? '-'), envXV, yy, { align: 'right' });
    doc.text(r.u, envXV + 28, yy);
    yy += rowH;
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  const bottom = [
    'Los resultados contenidos en el presente certificado se refieren exclusivamente a los equipos ó instrumentos sometidos a',
    'calibración ó medición, así como al momento y condiciones en que se realizaron las mediciones.',
    'El Laboratorio que lo emite no se responsabiliza de os perjuicios que puedan derivarse del uso inadecuado de este',
    'certificado.',
  ];
  let by = 262;
  bottom.forEach((t) => {
    doc.text(t, 14, by);
    by += 4.6;
  });
}

function computeMetrics(results) {
  const absErrors = results
    .map((r) => Math.abs(asNumber(r.error_abs) ?? 0))
    .filter((v) => Number.isFinite(v));
  const maxAbsError = absErrors.length ? Math.max(...absErrors) : 0;

  const uVals = results
    .map((r) => asNumber(r.u_reported))
    .filter((v) => Number.isFinite(v));
  const maxU = uVals.length ? Math.max(...uVals) : 0;

  return { maxAbsError, maxU };
}

function drawDeviationChartTemplate(doc, { x, y, w, h, points, errorPct, uReportedPct, tolPct }) {
  doc.setLineWidth(0.2);
  doc.setDrawColor(0, 0, 0);
  doc.rect(x, y, w, h);

  const minY = -7;
  const maxY = 3;
  const plotX0 = x + 18;
  const plotX1 = x + w - 10;
  const plotY0 = y + 12;
  const plotY1 = y + h - 16;

  // Grid
  doc.setDrawColor(170, 170, 170);
  doc.setLineWidth(0.1);
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const val = minY + ((maxY - minY) * i) / steps;
    const yy = plotY1 - ((val - minY) / (maxY - minY)) * (plotY1 - plotY0);
    doc.line(plotX0, yy, plotX1, yy);
  }

  // y labels
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  for (let v = minY; v <= maxY; v += 1) {
    const yy = plotY1 - ((v - minY) / (maxY - minY)) * (plotY1 - plotY0);
    doc.text(`${v},0%`, plotX0 - 6, yy + 2, { align: 'right' });
  }

  // x labels
  const n = Math.max(1, points.length);
  for (let i = 0; i <= n; i++) {
    const xx = plotX0 + (i * (plotX1 - plotX0)) / n;
    doc.setDrawColor(120, 120, 120);
    doc.line(xx, plotY1, xx, plotY1 + 2);
    doc.text(String(i), xx, plotY1 + 7, { align: 'center' });
  }

  // Axis titles
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Puntos de Calibración', x + w / 2, y + h - 4, { align: 'center' });

  const toX = (i) => plotX0 + ((i + 1) * (plotX1 - plotX0)) / n;
  const toY = (v) => plotY1 - ((v - minY) / (maxY - minY)) * (plotY1 - plotY0);

  // Red tolerance lines
  if (Number.isFinite(tolPct) && tolPct > 0) {
    doc.setDrawColor(200, 0, 0);
    doc.setLineWidth(0.45);
    const yTop = toY(Math.min(maxY, Math.abs(tolPct)));
    const yBot = toY(Math.max(minY, -Math.abs(tolPct)));
    doc.line(toX(0) - 2, yTop, toX(n - 1) + 2, yTop);
    doc.line(toX(0) - 2, yBot, toX(n - 1) + 2, yBot);
  }

  // uncertainty bars
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  for (let i = 0; i < n; i++) {
    const e = asNumber(errorPct[i]) ?? 0;
    const u = Math.abs(asNumber(uReportedPct[i]) ?? 0);
    const xx = toX(i);
    const y1 = toY(Math.max(minY, e - u));
    const y2 = toY(Math.min(maxY, e + u));
    doc.line(xx, y1, xx, y2);
    doc.line(xx - 1.2, y1, xx + 1.2, y1);
    doc.line(xx - 1.2, y2, xx + 1.2, y2);
  }

  // error line
  doc.setDrawColor(0, 70, 140);
  doc.setLineWidth(0.5);
  for (let i = 1; i < n; i++) {
    doc.line(toX(i - 1), toY(asNumber(errorPct[i - 1]) ?? 0), toX(i), toY(asNumber(errorPct[i]) ?? 0));
  }

  // green dots
  doc.setDrawColor(0, 70, 140);
  doc.setFillColor(110, 180, 110);
  doc.setLineWidth(0.2);
  for (let i = 0; i < n; i++) {
    doc.circle(toX(i), toY(asNumber(errorPct[i]) ?? 0), 1.8, 'FD');
  }
}

function getReferenceDescription(session) {
  switch (session?.template_code) {
    case 'PRESSURE_CPH6600_R09':
      return 'Calibrador con transmisor de presion interno WIKA CPH6600';
    case 'PRESSURE_CPH6000_R14':
    default:
      return 'Calibrador Portatil de Presion WIKA CPH6000';
  }
}

function drawPage2(doc, { session, results }) {
  const unit = session.instrument_unit || 'bar';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('RESULTADOS OBTENIDOS', 14, 45);

  autoTable(doc, {
    startY: 52,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 10, halign: 'center', valign: 'middle', lineWidth: 0.1, lineColor: [0, 0, 0] },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    bodyStyles: { textColor: [0, 0, 0] },
    theme: 'grid',
    head: [
      ['Pto. de\nMedición', 'Patrón', 'Instrumento', 'Histéresis', 'Error Instr.', 'Error Instr.', 'Incert.\nExpandida'],
      ['', `[${unit}]`, `[${unit}]`, `[${unit}]`, `[${unit}]`, '%', `[${unit}]`],
    ],
    body: results.map((r, idx) => [
      String(r.point_no ?? idx + 1),
      fmtNumber(r.pattern_mean, 3),
      fmtNumber(r.instrument_mean, 2),
      fmtNumber(r.hysteresis, 3),
      fmtNumber(r.error_abs, 3),
      fmtPct(r.error_pct, 2),
      fmtNumber(r.u_reported, 3),
    ]),
  });

  const { maxAbsError, maxU } = computeMetrics(results);
  const afterTableY = (doc.lastAutoTable?.finalY ?? 100) + 10;
  const metricValueX = 104;
  const metricUnitX = 114;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Error sistemático Mayor:', 22, afterTableY);
  doc.setFont('helvetica', 'normal');
  doc.text(fmtNumber(maxAbsError, 3), metricValueX, afterTableY, { align: 'right' });
  doc.text(`[${unit}]`, metricUnitX, afterTableY);

  doc.setFont('helvetica', 'bold');
  doc.text('Incertidumbre expandida', 22, afterTableY + 7);
  doc.setFont('helvetica', 'normal');
  doc.text(fmtNumber(maxU, 3), metricValueX, afterTableY + 7, { align: 'right' });
  doc.text(`[${unit}]`, metricUnitX, afterTableY + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('DESVIACIÓN', 14, afterTableY + 30);

  const points = results.map((r, i) => asNumber(r.point_no) ?? i + 1);
  const errPct = results.map((r) => asNumber(r.error_pct) ?? 0);
  const uPct = results.map((r) => {
    const p = asNumber(r.pattern_mean);
    const u = asNumber(r.u_reported);
    if (!p || !u) return 0;
    if (p === 0) return 0;
    return (u / Math.abs(p)) * 100;
  });

  const tolPct = (() => {
    const a = asNumber(session.instrument_class ?? session.instrument_accuracy_class);
    if (a !== null) return Math.abs(a);
    const b = asNumber(session.instrument_tolerance_pct);
    return b !== null ? Math.abs(b) : null;
  })();

  drawDeviationChartTemplate(doc, {
    x: 14,
    y: afterTableY + 35,
    w: 186,
    h: 85,
    points,
    errorPct: errPct,
    uReportedPct: uPct,
    tolPct,
  });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const bottom = [
    'Los resultados contenidos en el presente certificado se refieren exclusivamente a los equipos ó instrumentos sometidos a',
    'calibración ó medición, así como al momento y condiciones en que se realizaron las mediciones.',
    'El Laboratorio que lo emite no se responsabiliza de os perjuicios que puedan derivarse del uso inadecuado de este',
    'certificado.',
  ];
  let by = 262;
  bottom.forEach((t) => {
    doc.text(t, 14, by);
    by += 4.6;
  });
}

function sanitizeObservations(text) {
  if (!text) return '';
  const banned = [
    'FUNCIONA, PERO NO ESTÁ DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE',
    'FUNCIONA, PERO NO ESTA DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE',
    'FUNCIONA Y ESTA DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE',
    'FUNCIONA Y ESTÁ DENTRO DE LA BANDA DECLARADA POR EL FABRICANTE',
  ];
  let t = String(text);
  banned.forEach((b) => {
    t = t.split(b).join('');
  });
  t = t.replace(/\s+/g, ' ').trim();
  return t;
}

function drawPage3(doc, { session, signatureDataUrl }) {
  // Layout tuning (avoid signature glued to "Histéresis")
  const FOOTER_SAFE_TOP = 250;
  const DEF_TITLE_FS = 11.2;
  const DEF_BODY_FS = 10.2;
  const DEF_BLOCK_STEP = 18;
  const DEF_BODY_OFFSET = 6;
  const SIGN_Y_NAME = 236;
  const SIGN_Y_ROLE = 241;
  const SIGN_IMG_W = 44.4;
  const SIGN_IMG_H = 29.1;
  const SIGN_IMG_Y = 221.7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('OBSERVACIONES', 14, 45);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  const obsBase =
    'El valor de la incertidumbre expandida fue calculado multiplicando la incertidumbre estándar\n' +
    'combinada por el factor de cobertura k=2 correspondiente a un nivel de confianza cercano al 95%.';
  doc.text(obsBase, 14, 58);

  const extraObs = sanitizeObservations(session.observations || session.observations_text || session.notes);
  if (extraObs) {
    doc.text(extraObs, 14, 73, { maxWidth: 186 });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('MÉTODO Y PROCEDIMIENTO', 14, 95);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.text(`Por comparación con instrumento patrón. Según Procedimiento Técnico ${PROCEDURE_CODE}.`, 14, 103);

  const patternTableEnd = drawReferenceStandardsTable(doc, {
    startY: 120,
    references: [{
      usage: 'Referencia',
      description: session.pattern_name || getReferenceDescription(session),
      serial: session.pattern_serial,
      certificateNo: session.pattern_certificate,
      calibrationDate: session.pattern_calibration_date,
      validUntil: session.pattern_valid_until || session.pattern_due_date,
      scope: session.pressure_reference_unit ? `Según certificado · ${session.pressure_reference_unit}` : 'Según certificado',
    }],
  });

  let dy = patternTableEnd + 9;
  const defBlock = [
    { h: 'Posición del instrumento calibrado', t: sanitizeObservations(session.instrument_position || 'Posición vertical.') },
    {
      h: 'Error del instrumento',
      t:
        'Es el valor obtenido de la diferencia entre el promedio de las indicaciones del instrumento y la presión\n' +
        'indicada por el patrón para un mismo punto considerado.',
    },
    {
      h: 'Indicaciones de presión',
      t: 'Patrón: valor de presión de referencia. Instrumento: valor indicado por el equipo sometido a calibración.',
    },
    {
      h: 'Histéresis',
      t:
        'Es la diferencia de indicación obtenida para un mismo punto, entre la carrera ascendente y la\n' +
        'descendente.',
    },
  ];

  for (const b of defBlock) {
    if (dy > FOOTER_SAFE_TOP - 35) break;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(DEF_TITLE_FS);
    doc.text(b.h, 14, dy);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(DEF_BODY_FS);
    doc.text(b.t, 14, dy + DEF_BODY_OFFSET, { maxWidth: 186 });

    dy += DEF_BLOCK_STEP;
  }

  // Firma del director t?cnico (imagen + texto)
  if (signatureDataUrl) {
    doc.addImage(signatureDataUrl, 'PNG', 100 - SIGN_IMG_W / 2, SIGN_IMG_Y, SIGN_IMG_W, SIGN_IMG_H, undefined, 'FAST');
  }
// Texto legal inferior
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  const bottom = [
    'Los resultados contenidos en el presente certificado se refieren exclusivamente a los equipos ó instrumentos sometidos a',
    'calibración ó medición, así como al momento y condiciones en que se realizaron las mediciones.',
    'El Laboratorio que lo emite no se responsabiliza de os perjuicios que puedan derivarse del uso inadecuado de este',
    'certificado.',
  ];
  let by = 262;
  bottom.forEach((t) => {
    doc.text(t, 14, by);
    by += 4.6;
  });
}

export async function generatePressureNoSacCertificatePdf({ session, instrument, results = [] }, opts = {}) {
  if (!session) throw new Error('session es requerida');
  if (!results.length) throw new Error('No hay resultados para certificar');

  const pages = 3;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const logo = await fetchImageAsDataUrl(opts.logoUrl || LOGO_URL);
  const signature = await fetchImageAsDataUrl(opts.signatureUrl || SIGNATURE_URL);
  const watermarkLogo = ENABLE_WATERMARK
    ? await fetchImageAsDataUrl(opts.watermarkLogoUrl || WATERMARK_LOGO_URL)
    : null;

  const resolveCertificateNumber = (...values) => {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (/^\d+$/.test(text)) return text;
      const match = text.match(/OS-(\d+)-(\d+)$/i);
      if (match) return `${match[1]}${match[2]}`;
    }
    return '-';
  };

  const certNo = resolveCertificateNumber(
    session?.service_order_code,
    session?.serviceOrderCode,
    instrument?.lab_id,
    instrument?.labId,
    session?.certificate_number,
    session?.certificateNumber,
    session?.number
  );

  // Page 1
  drawHeaderTemplate(doc, { logoDataUrl: logo, certificateNumber: certNo, page: 1, pages, variant: 'page1' });
  if (watermarkLogo) drawWatermark(doc, watermarkLogo);
  drawPage1(doc, { session, instrument });

  // Page 2
  doc.addPage();
  drawHeaderTemplate(doc, { logoDataUrl: logo, certificateNumber: certNo, page: 2, pages, variant: 'inner' });
  if (watermarkLogo) drawWatermark(doc, watermarkLogo);
  drawPage2(doc, { session, results });
  // AutoTable can repaint the page graphics state in some PDF renderers.
  // Draw the compact inner header once more so the logo and page number remain intact.
  drawHeaderTemplate(doc, { logoDataUrl: logo, certificateNumber: certNo, page: 2, pages, variant: 'inner' });

  // Page 3
  doc.addPage();
  drawHeaderTemplate(doc, { logoDataUrl: logo, certificateNumber: certNo, page: 3, pages, variant: 'inner' });
  if (watermarkLogo) drawWatermark(doc, watermarkLogo);
  drawPage3(doc, { session, signatureDataUrl: signature });

  // Footer
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    drawFooterTemplate(doc, p, pages);
  }

  if (opts.returnBlob) {
    const ab = doc.output('arraybuffer');
    return new Blob([ab], { type: 'application/pdf' });
  }

  const fallbackName = `R-DT-PC-${certNo || session.id || 'SIN-NRO'}.pdf`;
  doc.save(opts.filename || fallbackName);
  return null;
}








